import type { Auth } from 'firebase/auth';
import { type Firestore, doc, getDoc, updateDoc } from 'firebase/firestore';

import type {
  EmployeePort,
  MyEmployeeRecord,
  MyProfileUpdate,
} from '../../features/employees/application/ports/employeePort';
import { EmployeeError } from '../../features/employees/application/ports/employeePort';

/**
 * Firestore implementation of `EmployeePort` (blueprint §11.1).
 *
 * Resolves the caller's own record through their account link, which is also
 * how the security rules resolve it — the two agree by construction rather
 * than by coincidence.
 *
 * Raw platform errors are translated and never escape (§4.5).
 */

function translate(error: unknown): EmployeeError {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';

  if (code.includes('permission-denied')) {
    return new EmployeeError('forbidden', 'لا تملك صلاحية الوصول لهذا السجل.');
  }
  if (code.includes('unavailable') || code.includes('deadline')) {
    return new EmployeeError('network', 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.');
  }
  return new EmployeeError('unknown', 'تعذّر إتمام العملية. حاول مرة أخرى.');
}

/** Firestore's DocumentData is indexed `any`; every read is narrowed here. */
function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export function createFirebaseEmployeeAdapter(
  firestore: Firestore,
  auth: Auth,
): EmployeePort {
  async function resolveMyEmployeeId(): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new EmployeeError('forbidden', 'يجب تسجيل الدخول أولًا.');

    const link = await getDoc(doc(firestore, 'userAccounts', uid));
    const employeeId: unknown = link.data()?.['employeeId'];

    if (typeof employeeId !== 'string') {
      throw new EmployeeError('not_found', 'لا يوجد سجل موظف مرتبط بحسابك.');
    }
    return employeeId;
  }

  return {
    async getMyRecord(): Promise<MyEmployeeRecord> {
      try {
        const employeeId = await resolveMyEmployeeId();
        const snapshot = await getDoc(doc(firestore, 'employees', employeeId));

        if (!snapshot.exists()) {
          throw new EmployeeError('not_found', 'لم يُعثر على سجلك الوظيفي.');
        }

        const data = snapshot.data();
        const gender = readString(data, 'gender');
        const children: unknown = data['declaredChildrenCount'];
        const married: unknown = data['declaredMaritalStatus'];

        return {
          id: snapshot.id,
          employeeNumber: readString(data, 'employeeNumber') ?? snapshot.id,
          fullName: readString(data, 'fullName') ?? '',
          branchId: readString(data, 'branchId'),
          phone: readString(data, 'phone'),
          address: readString(data, 'address'),
          workEmail: readString(data, 'workEmail'),
          nationalId: readString(data, 'nationalId'),
          birthDate: readString(data, 'birthDate'),
          gender: gender === 'male' || gender === 'female' ? gender : null,
          declaredMaritalStatus: typeof married === 'boolean' ? married : null,
          declaredChildrenCount: typeof children === 'number' ? children : 0,
          enrolledInFund: data['enrolledInFund'] === true,
        };
      } catch (error) {
        throw error instanceof EmployeeError ? error : translate(error);
      }
    },

    async updateMyProfile(update: MyProfileUpdate): Promise<void> {
      try {
        const employeeId = await resolveMyEmployeeId();

        // Exactly the fields the rules permit an owner to change. Sending one
        // more would be refused by the rules, which is the point: this list and
        // that list are the same list, in two places, on purpose.
        await updateDoc(doc(firestore, 'employees', employeeId), {
          nationalId: update.nationalId,
          birthDate: update.birthDate,
          gender: update.gender,
          phone: update.phone,
          address: update.address,
          profileCompletedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          updatedBy: employeeId,
        });
      } catch (error) {
        throw error instanceof EmployeeError ? error : translate(error);
      }
    },
  };
}
